import { IButtonProperties } from '@vfde-brix/ws10/button';
import { getCMSConfig } from '../cms';
import {
    CmsAvcResult,
    JarvisProduct,
} from '../../interface';
import { ProductType } from '../../enum';
import {
    getOptionPageUrl,
    getProductPageUrl,
} from '../url';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { setOverlayVisibility } from '../../components/overlay';
import { updateFloatingMobileNotification } from '../../components/fullwidthNotification';

/**
 * Get the properties for offer summary card button.
 *
 * @param tariff - Best available (selected) tariff
 * @param isGigakombi - If user has selected gigakombi.
 * @param isYoung - If user has selected young option.
 * @param isAvailabilityChecked - If avc is done.
 * @param isTVProductSelected - If TV Connect is selected.
 * @param isExistingKipCustomer - If Customer is existing Cable customer
 * @param onClick - On click action to handle overlay opening.
 * @returns - The properties to update the button component.
 */
export const getOfferSummaryCardButtonProps = (
    tariff: JarvisProduct | undefined,
    isGigakombi: boolean,
    isYoung: boolean,
    isAvailabilityChecked: boolean,
    isTVProductSelected: boolean,
    isExistingKipCustomer: boolean,
    onClick: () => void,
): Partial<IButtonProperties> => {
    const { avc, available, unavailable, existingKip } = getCMSConfig().offerSummaryCard.button;

    if (!isAvailabilityChecked) {
        updateFloatingMobileNotification(true);

        return {
            stdContent: avc,
            business: { onClick },
            linkHref: undefined,
        };
    }

    if (isExistingKipCustomer) {
        updateFloatingMobileNotification(false);

        return {
            stdContent: existingKip,
            linkHref: getCMSConfig().contentUrls.cableChange,
            business: NO_PATTERN_BUSINESS_LOGIC,
        };
    }

    switch (tariff?.productType) {
        case ProductType.CABLE:
            updateFloatingMobileNotification(true);

            return isTVProductSelected ? {
                stdContent: available,
                linkHref: undefined,
                business: { onClick },
            } : {
                stdContent: available,
                linkHref: getOptionPageUrl(tariff, isGigakombi, isYoung),
                business: NO_PATTERN_BUSINESS_LOGIC,
            };
        default:
            updateFloatingMobileNotification(false);

            return {
                stdContent: unavailable,
                linkHref: getProductPageUrl(tariff?.id, tariff?.productType, isGigakombi, isYoung),
                business: NO_PATTERN_BUSINESS_LOGIC,
            };
    }
};

/**
 * Button with href link to the OptionPage
 */
export const getOptionPageBtnProps = (
    tariff: JarvisProduct,
    isGigakombi: boolean,
    isYoung: boolean,
    withTvConnect = false,
): Partial<IButtonProperties> => ({
    linkHref: getOptionPageUrl(tariff, isGigakombi, isYoung, withTvConnect),
    business: NO_PATTERN_BUSINESS_LOGIC,
});

const getCableAvcResultBtnProps = (
    config: CmsAvcResult,
    isTVProductSelected: boolean,
    isGigakombi: boolean,
    isYoung: boolean,
    product: JarvisProduct,
    isSelectedAvailable: boolean,
): Partial<IButtonProperties> => {
    const props: Partial<IButtonProperties> = {
        linkHref: undefined,
        business: { onClick: () => setOverlayVisibility(false) },
    };

    if (isTVProductSelected) {
        props.stdContent = config.cable.available.buttonTvConnect;
    }
    else if (!isSelectedAvailable) {
        props.stdContent = config.cable.available.buttonSlower;
    }
    else {
        props.stdContent = config.cable.available.button;
    }

    return props;
};

/**
 * Gets the props that need to be
 * updated for the avc result button
 *
 * @param isGigakombi - Gigakombi chosen or not
 * @param isYoung - Young chosen or not
 * @param isTVProductSelected - Tv product is selected or not
 * @param isSelectedAvailable - Selected tariff is available or not
 * @param product - Fastest available product
 */
export const getAvcResultButtonProps = (
    isGigakombi: boolean,
    isYoung: boolean,
    isTVProductSelected: boolean,
    isSelectedAvailable: boolean,
    product?: JarvisProduct,
): Partial<IButtonProperties> => {
    const config = getCMSConfig().avcResult;

    switch (product?.productType) {
        case ProductType.CABLE:
            return getCableAvcResultBtnProps(config, isTVProductSelected, isGigakombi, isYoung, product, isSelectedAvailable);
        case ProductType.FIBRE:
            return {
                linkHref: getProductPageUrl(product.id, product.productType, isGigakombi, isYoung),
                stdContent: config.fiber.button,
                business: NO_PATTERN_BUSINESS_LOGIC,
            };
        case ProductType.DSL:
            return {
                linkHref: getProductPageUrl(product.id, product.productType, isGigakombi, isYoung),
                stdContent: config.dsl.button,
                business: NO_PATTERN_BUSINESS_LOGIC,
            };
        default:
            return {
                linkHref: config.gigacube.linkHref,
                stdContent: config.gigacube.button,
                business: NO_PATTERN_BUSINESS_LOGIC,
            };
    }
};
